import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPinLine } from "@phosphor-icons/react";
import { http, API_BASE } from "@/lib/api";
import TrustBadge from "@/components/TrustBadge";
import SocialPulseBadge from "@/components/SocialPulseBadge";

// Recent Discoveries — three verified sightings surfaced on the home page.
// Now wired to `/api/nature/observations?moderation_status=approved` and
// falls back to a curated seed set when the feed is empty.

const FALLBACK = [
  {
    n: 1,
    id: "seed-1",
    title: "Belted Kingfisher sighting",
    place: "Portland OR",
    coords: "45.52, -122.68",
    time: "05:22 AM",
    image: "https://images.unsplash.com/photo-1444464666168-49d633b86797?w=1200&auto=format&fit=crop",
    href: "/observations",
    kind: "wildlife",
    trusted: false,
    is_seed: true,
  },
  {
    n: 2,
    id: "seed-2",
    title: "Flowering Douglas Fir",
    place: "Mt. Hood OR",
    coords: "45.37, -121.70",
    time: "11:04 AM",
    image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1200&auto=format&fit=crop",
    href: "/observations",
    kind: "trees",
    trusted: false,
    is_seed: true,
  },
  {
    n: 3,
    id: "seed-3",
    title: "Tidal Surge Report",
    place: "Coast OR",
    coords: "45.72, -123.94",
    time: "12:03 AM",
    image: "https://images.unsplash.com/photo-1505142468610-359e7d316be0?w=1200&auto=format&fit=crop",
    href: "/observations",
    kind: "oceans",
    trusted: false,
    is_seed: true,
  },
];

// Same test-pin filter used by /map — skip seed observations at ~(0,0).
const looksReal = (o) => {
  const lat = o?.location?.lat;
  const lon = o?.location?.lon;
  const media = (o?.media || [])[0]?.url;
  const title = (o?.title || "").toLowerCase();
  if (!media) return false;
  if (title.startsWith("test_") || title.includes("test_refactor") || title.includes("testville")) return false;
  if (typeof lat === "number" && typeof lon === "number" && Math.abs(lat) < 1.5 && Math.abs(lon) < 1.5) return false;
  return true;
};

const abs = (u) => (!u ? "" : u.startsWith("http") ? u : `${API_BASE}${u}`);

const fmtTime = (o) => {
  const t = o?.time;
  if (t && /^\d{1,2}:\d{2}/.test(t)) return t;
  const iso = o?.created_at || o?.date;
  try {
    if (iso) return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch { /* noop */ }
  return "";
};

const fmtPlace = (o) => {
  const loc = o?.location || {};
  return [loc.city || loc.village, loc.state || loc.country].filter(Boolean).join(", ") || (loc.display_name || "");
};

const fmtCoords = (o) => {
  const { lat, lon } = o?.location || {};
  if (typeof lat === "number" && typeof lon === "number") return `${lat.toFixed(2)}, ${lon.toFixed(2)}`;
  return null;
};

export default function RecentDiscoveries() {
  const [items, setItems] = useState(FALLBACK);
  const [fromApi, setFromApi] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await http.get("/nature/observations", { params: { limit: 24 } });
        const raw = data?.observations || [];
        const real = raw.filter(looksReal).slice(0, 3);
        if (!alive) return;
        if (real.length === 3) {
          setItems(real.map((o, i) => ({
            n: i + 1,
            id: o.id,
            title: o.title || "Untitled sighting",
            place: fmtPlace(o),
            coords: fmtCoords(o),
            time: fmtTime(o),
            image: abs((o.media || [])[0]?.url) || FALLBACK[i].image,
            href: `/observations/${o.id}`,
            kind: o.category || (o.category_kinds || [])[0] || "nature",
            trusted: Array.isArray(o.trust_labels) && o.trust_labels.includes("trusted-contributor"),
            trust_kind: o.trust_kind || (Array.isArray(o.trust_labels) && o.trust_labels.includes("trusted-contributor") ? "expert" : "community"),
            sensitive_location: !!o.sensitive_location,
            author: o.reporter?.name,
            author_provider: o.reporter?.provider,
            is_seed: false,
          })));
          setFromApi(true);
        }
      } catch { /* keep fallback */ }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <section className="bg-[#0C2D1F] text-[color:var(--np-cream,#F5EBDC)] py-14 md:py-20 border-t border-[#F5EBDC]/10">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex items-baseline justify-between mb-8">
          <h2 className="font-mono-tech text-[11px] md:text-xs tracking-[0.35em] uppercase text-[#E5D8C4]/80">
            Recent Discoveries
          </h2>
          <span className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#E5D8C4]/50">
            /02 · {fromApi ? "Latest sightings" : "Verified sightings"}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {items.map((d, i) => {
            const Wrapper = d.href ? Link : "div";
            const wrapperProps = d.href ? { to: d.href, "data-testid": `discovery-link-${d.n}` } : {};
            return (
              <motion.article
                key={d.id || d.n}
                data-testid={`discovery-card-${d.n}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: i * 0.1, duration: 0.6 }}
                className="group"
              >
                <Wrapper {...wrapperProps} className="block">
                  <div className="relative aspect-[4/3] overflow-hidden border border-[#F5EBDC]/10">
                    <img
                      src={d.image}
                      alt={d.title}
                      onError={(e) => { e.target.src = FALLBACK[i % 3].image; }}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0C2D1F]/70 via-transparent to-transparent" />
                    <div className="absolute top-3 left-3 flex flex-col gap-1 items-start">
                      <TrustBadge
                        kind={d.trust_kind || (d.trusted ? "expert" : "community")}
                        size="xs"
                        className="bg-[#F7F3E8]/95 backdrop-blur-sm !border-black/20 !text-[#241209]"
                        testId={`discovery-badge-${d.n}`}
                      />
                      {d.sensitive_location && (
                        <div className="inline-flex items-center gap-1 px-2 py-1 bg-[#0C2D1F]/85 backdrop-blur-sm border border-[#E4A15C]/30" data-testid={`discovery-cloaked-${d.n}`}>
                          <MapPinLine size={11} weight="bold" className="text-[#E4A15C]" />
                          <span className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#E4A15C]">Location obscured</span>
                        </div>
                      )}
                    </div>
                    {d.time && (
                      <div className="absolute bottom-3 right-3 font-mono-tech text-[10px] tracking-[0.25em] uppercase text-[#F5EBDC]/90 bg-[#0C2D1F]/60 backdrop-blur-sm px-2 py-1">
                        {d.time}
                      </div>
                    )}
                  </div>
                  <div className="mt-4">
                    <div className="font-serif-display text-lg leading-snug text-[#F5EBDC]">
                      {d.n}. {d.title},
                    </div>
                    <div className="font-serif-display text-base text-[#E5D8C4]/80">
                      {d.place || "—"} {d.coords && <span className="text-[#E5D8C4]/50">({d.coords})</span>}
                    </div>
                    {d.author && (
                      <div className="mt-1 font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#E5D8C4]/55 flex items-center gap-1.5">
                        by {d.author} <SocialPulseBadge provider={d.author_provider} testid={`sp-badge-${d.id || d.n}`} />
                      </div>
                    )}
                  </div>
                </Wrapper>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
